/*
 * *****************************************************************************
 *  Copyright (C)  Motorola Solutions, INC.
 *  All Rights Reserved.
 *  Motorola Solutions Confidential Restricted.
 *  ******************************************************************************
 */

import { environment } from "../../../environments/environment";
import { Message } from "CalltakingCoreApi";
import { SortFunctions } from "../../call/util/sort-functions";
import { TranslationLanguage } from "../model/translation.model";
import { LanguageChangeSource } from "../model/language.model";

export function deriveLanguageChangeSource(messageSenderType: 'AGENT' | 'EXTERNAL' | string): LanguageChangeSource {
    return messageSenderType === 'AGENT' ? LanguageChangeSource.Manual : LanguageChangeSource.Automatic;
}

export function deriveLanguageAndSource(
    messageSenderType: 'AGENT' | 'EXTERNAL' | string,
    languageCode: string
): { languageCode: string; languageChangeSource: LanguageChangeSource } {
    return { languageCode, languageChangeSource: deriveLanguageChangeSource(messageSenderType) };
}

export function isLanguageExplicitlySetToNonDefault(lang: string | null | undefined): boolean {
    return Boolean(lang && lang !== environment.defaultLanguage);
}

export function isLanguageEqualsDefault(lang: string | null | undefined): boolean {
    return lang === environment.defaultLanguage;
}

export function getLanguageDisplayName(
    languageCode: string,
    locale: string = environment.defaultLanguage
): string {
    const displayNames = new Intl.DisplayNames([locale], { type: 'language' });
    return displayNames.of(languageCode) ?? languageCode;
}

export function hasTranslation(message: Message): boolean {
    return Boolean(message?.sourceText || message?.targetText);
}

export interface MessageTranslationView {
    isTranslated: boolean;
    topCode: string | undefined;
    bottomCode: string | undefined;
}

export function getMessageTranslationView(message: Message): MessageTranslationView {
    const isTranslated = hasTranslation(message);
    if (!isTranslated) {
        return { isTranslated: false, topCode: undefined, bottomCode: undefined };
    }
    return {
        isTranslated: true,
        topCode: message.sourceText ? message.sourceLanguage : message.messageLanguage,
        bottomCode: message.sourceText ? message.messageLanguage : message.targetLanguage
    };
}

export interface LanguageTimelineEntry {
    languageCode: string;
    changeSource: LanguageChangeSource;
    originMessageUuid: string;
    timestamp: number;
    isFirst: boolean;
}

export function computeMessageLanguageChanges(messages: Message[]): LanguageTimelineEntry[] {
    const timeline: LanguageTimelineEntry[] = [];
    let lastLangKey: string | null = null;

    const sorted = [...(messages || [])].sort(
        (a, b) => new Date(a.createdTimestamp as any).getTime() - new Date(b.createdTimestamp as any).getTime()
    );

    for (const msg of sorted) {
        if (!hasTranslation(msg)) continue;

        const foreignLang = msg.messageLanguage;
        if (!foreignLang || isLanguageEqualsDefault(foreignLang)) continue;

        if (foreignLang === lastLangKey) {
            continue;
        }

        const changeSource = deriveLanguageChangeSource(msg.messageSenderType === 'AGENT' ? 'AGENT' : 'EXTERNAL');
        const entry: LanguageTimelineEntry = {
            languageCode: foreignLang,
            changeSource,
            originMessageUuid: msg.uuid,
            timestamp: new Date(msg.createdTimestamp as any).getTime(),
            isFirst: lastLangKey === null
        };
        timeline.push(entry);
        lastLangKey = foreignLang;
    }

    return timeline;
}

export function mapCodesToTranslationLanguages(codes: string[], available: TranslationLanguage[]): TranslationLanguage[] {
    const unique = codes.filter((v, i, a) => a.indexOf(v) === i);
    return unique.map((code) => available
        .find((l) => l.languageCode === code) ?? { languageCode: code, name: code });
}

export function selectMessagesBySender(
    messages: Message[],
    senderType: 'AGENT' | 'EXTERNAL' | Array<'AGENT' | 'EXTERNAL'>,
    options?: { requireTranslation?: boolean; requireTargetText?: boolean; sortOrder?: 'oldest' | 'newest' }
): Message[] {
    const senderTypes = Array.isArray(senderType) ? senderType : [senderType];

    const filtered = messages
        .filter((m) =>
            // keep only requested sender type(s) (AGENT and/or EXTERNAL)
            senderTypes.includes(m.messageSenderType as 'AGENT' | 'EXTERNAL') &&
            // skip messages without a detected/assigned language
            m.messageLanguage &&
            // for AGENT only: require a translation (sourceText/targetText) when requested
            (!options?.requireTranslation || m.messageSenderType !== 'AGENT' || hasTranslation(m)) &&
            (!options?.requireTargetText || hasTranslation(m))
        );

    if (options?.sortOrder === 'newest') {
        return [...filtered].sort(SortFunctions.newestCreatedSort);
    }
    if (options?.sortOrder === 'oldest') {
        return [...filtered].sort(SortFunctions.oldestCreatedSort);
    }
    return filtered;
}
